// modal-context.tsx
// Modal navigation state management for billing modals

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  ModalContextValue,
  BillingModalName,
  NavigationDirection,
  BillingCycle,
} from './settings.types';

// Module-level ref so the settings hook can check if a billing modal is active
// (the hook runs before providers, so it can't use useContext).
export const billingModalOpenRef = { current: false };

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ModalContext = createContext<ModalContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface ModalProviderProps {
  children: React.ReactNode;
  open?: boolean;
}

export function ModalProvider({ children, open }: ModalProviderProps) {
  const [currentModal, setCurrentModal] = useState<BillingModalName>(null);
  const [navigationStack, setNavigationStack] = useState<string[]>([]);
  const [direction, setDirection] = useState<NavigationDirection>('forward');
  const [editCheckoutFlow, setEditCheckoutFlow] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<BillingCycle>('annual');
  const [selectedPlanName, setSelectedPlanName] = useState<string | null>(null);

  // Keep module-level ref in sync so settings hook can read it
  useEffect(() => {
    billingModalOpenRef.current = currentModal !== null;
  }, [currentModal]);

  // Reset modal state when settings close
  useEffect(() => {
    if (open === false) {
      setCurrentModal(null);
      setNavigationStack([]);
      setEditCheckoutFlow(false);
      billingModalOpenRef.current = false;
    }
  }, [open]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────────

  const openModal = useCallback(
    (
      modal: BillingModalName,
      options?: { checkoutFlow?: boolean; billingCycle?: BillingCycle; planName?: string }
    ) => {
      if (!modal) return;

      setDirection('forward');
      setCurrentModal(modal);
      setNavigationStack((prev) => [...prev, modal]);

      if (options?.checkoutFlow !== undefined) {
        setEditCheckoutFlow(options.checkoutFlow);
      }
      if (options?.billingCycle !== undefined) {
        setSelectedBillingCycle(options.billingCycle);
      }
      if (options?.planName !== undefined) {
        setSelectedPlanName(options.planName);
      }
    },
    []
  );

  const closeModal = useCallback(() => {
    setDirection('backward');
    setCurrentModal(null);
    setNavigationStack([]);
    setEditCheckoutFlow(false);
  }, []);

  const goBack = useCallback(() => {
    setDirection('backward');
    
    if (navigationStack.length > 1) {
      // Remove current modal from stack
      const newStack = navigationStack.slice(0, -1);
      setNavigationStack(newStack);
      setCurrentModal(newStack[newStack.length - 1] as BillingModalName);
    } else if (navigationStack.length === 1) {
      // Close the only modal
      setNavigationStack([]);
      setCurrentModal(null);
      setEditCheckoutFlow(false);
    }
  }, [navigationStack]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────────────────────

  const value: ModalContextValue = useMemo(
    () => ({
      currentModal,
      direction,
      editCheckoutFlow,
      selectedBillingCycle,
      selectedPlanName,
      openModal,
      closeModal,
      goBack,
    }),
    [currentModal, direction, editCheckoutFlow, selectedBillingCycle, selectedPlanName, openModal, closeModal, goBack]
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useModalContext(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider');
  }
  return context;
}
